/**
 * Shared coercion helpers used by every section schema validator.
 *
 * EPIC-13 · FEAT-13.2 · STORY-13.2.09.
 *
 * Pure JS, no Firebase imports. Each section helper (hero.js, editorial.js,
 * etc.) composes these into its own mergeX function so the unit-test
 * surface is small and the merge contract is uniform across sections.
 *
 * Contract:
 *   - All coercions are total: an arbitrary input never throws, never
 *     returns undefined. Bad inputs collapse to the supplied default.
 *   - Empty strings preserve as empty strings (operator can blank a
 *     field on purpose). null / undefined / non-strings fall back to
 *     the default.
 *   - clampArr applies (a) shape coercion to each item, (b) drop-if-null,
 *     (c) length clamp [min,max]. Below min we top up with default
 *     items; above max we truncate.
 */

/**
 * Coerce a value to a trimmed string, capped at maxLen. Anything that
 * isn't a string falls back to `fallback`.
 */
export function clampStr(value, fallback, maxLen) {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.length > maxLen ? value.slice(0, maxLen) : value;
  // Note: we deliberately keep leading/trailing whitespace untouched —
  // a headline like "  whisper  " might be intentional kerning. Callers
  // that want a trim should do it explicitly.
  return trimmed;
}

/**
 * Coerce a value to one of `allowed`; fall back to `fallback` otherwise.
 * Used for enum fields like Press logo `variant`.
 */
export function clampEnum(value, allowed, fallback) {
  return allowed.includes(value) ? value : fallback;
}

/**
 * Apply `coerceItem` to each entry, drop null/undefined results, then
 * clamp the array to [min, max]. Behaves differently for two failure
 * modes that need to feel distinct to an operator:
 *
 *   • No input at all (`value` is null/undefined/non-array): treat as
 *     "operator never touched this field" and return the canonical
 *     full DEFAULT list (length = `defaultItems.length`).
 *   • Empty or all-rows-drop array: treat as "operator cleared the
 *     list explicitly" and top up to `min` from defaultItems, but DO
 *     NOT restore the full default — the operator chose this scope.
 *
 * This keeps `merge(null)` looking identical to the canonical default
 * (matching every operator's intuition for "fresh state") while still
 * honoring an explicit clear-to-empty with the smallest viable list.
 *
 * `coerceItem(raw, index)` should return the cleaned item, or `null` to
 * drop the row entirely (e.g. missing required field).
 */
export function clampArr(value, { min, max, coerceItem, defaultItems }) {
  // No-input case → full default list (capped at max as a safety net).
  if (!Array.isArray(value)) {
    if (!Array.isArray(defaultItems) || defaultItems.length === 0) return [];
    return defaultItems.slice(0, max).map((item) =>
      item && typeof item === 'object' ? { ...item } : item,
    );
  }
  // Operator-provided array path — coerce, drop nulls, truncate, top up.
  const cleaned = [];
  for (let i = 0; i < value.length && cleaned.length < max; i++) {
    const item = coerceItem(value[i], i);
    if (item != null) cleaned.push(item);
  }
  if (cleaned.length < min && Array.isArray(defaultItems) && defaultItems.length > 0) {
    while (cleaned.length < min) {
      const fill = defaultItems[cleaned.length % defaultItems.length];
      cleaned.push(fill && typeof fill === 'object' ? { ...fill } : fill);
    }
  }
  return cleaned;
}

/**
 * Build an object with exactly the keys listed in `schema` — anything
 * not listed is dropped. Each key maps to a coerce function that takes
 * (raw[key], raw) and returns the cleaned value.
 */
export function pickKeys(raw, schema) {
  const src = (raw && typeof raw === 'object') ? raw : {};
  const out = {};
  for (const key of Object.keys(schema)) {
    out[key] = schema[key](src[key], src);
  }
  return out;
}

/**
 * Convenience — coerce to a relative-or-absolute URL string. Doesn't
 * try to validate scheme; that's the operator's job (and Storage rules'
 * job for image refs). Caps at 1024 chars to avoid accidental MB-long
 * data URIs slipping through.
 */
export function clampUrl(value, fallback) {
  return clampStr(value, fallback, 1024);
}
